import { Table } from '@tanstack/react-table';
import { BmweDataColumn } from '../../../types';
import { useTableView } from '../context/tableView';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import styles from './tableViewControl.module.css';
import { Dialog } from 'primereact/dialog';
import { TableViewControlDialog } from './tableViewControlDialog';
import { useState } from 'react';

import { tableStrings } from '../i18n/de-de';
import { commonStrings } from '../../../i18n';
import { TableLayoutMode } from '../hooks/useTableLayout';

export interface TableViewControlProps<TRow extends Record<string, unknown>> {
  columns: BmweDataColumn<TRow>[];
  table?: Table<TRow>;
  layoutMode?: TableLayoutMode;
  onToggleLayoutMode?: () => void;
  onAutoFitAll?: () => void;
}

export function TableViewControl<TRow extends Record<string, unknown>>({
  columns,
  table,
  layoutMode,
  onToggleLayoutMode,
  onAutoFitAll,
}: TableViewControlProps<TRow>) {
  const {
    currentView,
    views,
    setCurrentViewById,
    createSnapshot,
    clearSnapshot,
    hasSnapshot,
    columnFilters,
    setFilters,
  } = useTableView();
  const [visible, setVisible] = useState(false);
  const [isCreatingNew, setIsCreatingNew] = useState(false);

  const handleDialogClose = (reset: boolean) => {
    setVisible(false);
    if (reset) {
      clearSnapshot();
    }
  };

  const handleDialogHide = () => {
    if (!visible) {
      return;
    }
    setVisible(false);
  };

  const handleViewChange = (id: { value: string }) => {
    setCurrentViewById(id.value);
  };

  const handleSettingsClick = () => {
    createSnapshot();
    setIsCreatingNew(false);
    setVisible(true);
  };

  const handleNewViewClick = () => {
    createSnapshot();
    setIsCreatingNew(true);
    setVisible(true);
  };

  return (
    <>
      <div className={styles.tableViewControl}>
        <Dropdown
          options={views}
          optionLabel="name"
          optionValue="id"
          value={currentView.id}
          aria-label={tableStrings.tableView.selectView}
          style={{
            width: 'min(400px, 100%)',
          }}
          onChange={(id) => {
            handleViewChange(id);
          }}
        />

        <div className={styles.tableViewActions}>
          {onToggleLayoutMode && (
            <Button
              title={
                layoutMode === 'scroll'
                  ? tableStrings.layout.fit
                  : tableStrings.layout.scroll
              }
              icon={
                layoutMode === 'scroll'
                  ? 'pi pi-arrows-h'
                  : 'pi pi-arrow-right-arrow-left'
              }
              label={
                layoutMode === 'scroll'
                  ? tableStrings.layout.fit
                  : tableStrings.layout.scroll
              }
              outlined
              severity="secondary"
              aria-pressed={layoutMode === 'scroll'}
              onClick={onToggleLayoutMode}
            />
          )}
          {onAutoFitAll && (
            <Button
              title={tableStrings.layout.autoFitAllTooltip}
              icon="pi pi-align-justify"
              label={tableStrings.layout.autoFitAll}
              outlined
              severity="secondary"
              onClick={onAutoFitAll}
            />
          )}
          {columnFilters.length > 0 && (
            <Button
              title={tableStrings.filter.clearAll}
              icon="pi pi-filter-slash"
              outlined
              severity="secondary"
              aria-label={tableStrings.filter.clearAll}
              onClick={() => setFilters([])}
            />
          )}
          <Button
            title={tableStrings.tableView.changeViews}
            icon="pi pi-cog"
            aria-label={tableStrings.tableView.changeViews}
            onClick={handleSettingsClick}
          />
          {!hasSnapshot && (
            <Button
              title={tableStrings.tableView.createNewView}
              icon="pi pi-plus"
              outlined
              aria-label={tableStrings.tableView.createNewView}
              onClick={handleNewViewClick}
            />
          )}
          {hasSnapshot && (
            <Button
              title={commonStrings.words.reset}
              icon="pi pi-refresh"
              severity="warning"
              outlined
              aria-label={commonStrings.words.reset}
              onClick={clearSnapshot}
            />
          )}
        </div>
      </div>
      <Dialog
        header={tableStrings.tableView.manageViews}
        visible={visible}
        style={{ width: 'min(860px, 100vw)' }}
        onHide={handleDialogHide}
      >
        <TableViewControlDialog
          columns={columns}
          table={table}
          close={handleDialogClose}
          isCreatingNew={isCreatingNew}
        />
      </Dialog>
    </>
  );
}
